import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { ResponsiveLine } from "@nivo/line";

import { SAMPLE_COST_SAVINGS } from "./sampleData";

import { getYear } from "date-fns";
import "./styles.css";
import { formatCurrencyNoDecimal } from "components/Global/Metric/formatters";
import numeral from "numeral";

const RefiAnalysisLineChart = ({ term, breakEvenResults, companyPrimaryColor, companySecondaryColor, providedExistingLoanInterestPaid, providedRefiLoanInterestPaid }) => {
  const { costSavings: breakevenResultsCostSavings = [] } = breakEvenResults || {};
  const [totalExistingLoanInterestPaid, setTotalCurrentLoanInterestPaid] = useState(0);
  const [totalRefiLoanInterestPaid, setTotalRefiLoanInterestPaid] = useState(0);
  const [chartData, setChartData] = useState([]);

  const runningAccumulatorReducer = (arr = []) => arr.reduce((r, a) => {
    if (r.length > 0) {
      a = a + r[r.length - 1];
    }
    r.push(a);

    return r;
  }, []);

  useEffect(() => {
    if (!providedRefiLoanInterestPaid && !providedExistingLoanInterestPaid) {
      const oldLoanInterestPayments = breakevenResultsCostSavings.map(payment => payment.oldInterest);
      const newLoanInterestPayments = breakevenResultsCostSavings.map(payment => payment.newInterest);
      const totalExistingLoanInterestPayments = oldLoanInterestPayments.reduce((a, b) => a + b, 0);
      const totalNewLoanInterestPayments = newLoanInterestPayments.reduce((a, b) => a + b, 0);
      setTotalCurrentLoanInterestPaid(totalExistingLoanInterestPayments);
      setTotalRefiLoanInterestPaid(totalNewLoanInterestPayments);

      const dataWithCumulativeResults = breakevenResultsCostSavings.map(saving => ({
        ...saving,
        year: getYear(new Date(saving.beginDate))
        /*
         * Only need these if we use all data points for every month
         * oldInterestCumulative: runningCumulativeOldInterestPayments[idx],
         * newInterestCumulative: runningCumulativeNewInterestPayments[idx],
         * interestCostSavingsCumulative: runningCumulativeCostSavings[idx],
         */
      }));

      const yearsOfData = dataWithCumulativeResults.map(d => d.year).filter((val, idx, a) => a.indexOf(val) === idx);
      const oldInterestReducedByYear = yearsOfData.map(year => dataWithCumulativeResults
        .filter(data => data.year === year)
        .map(d => d.oldInterest)
        .reduce((a, b) => a + b, 0));
      const newInterestReducedByYear = yearsOfData.map(year => dataWithCumulativeResults
        .filter(data => data.year === year)
        .map(d => d.oldInterest - d.interestCostSavings)
        .reduce((a, b) => a + b, 0));

      const runningCumulativeOldInterestReducedByYear = runningAccumulatorReducer(oldInterestReducedByYear);
      const runningCumulativeNewInterestReducedByYear = runningAccumulatorReducer(newInterestReducedByYear);

      /*
       * Only need these if we use all data points for every month.
       * const costSavings = breakevenResultsCostSavings.map(payment => payment.interestCostSavings);
       * const runningCumulativeOldInterestPayments = runningAccumulatorReducer(oldLoanInterestPayments);
       * const runningCumulativeNewInterestPayments = runningAccumulatorReducer(newLoanInterestPayments);
       * const runningCumulativeCostSavings = runningAccumulatorReducer(costSavings);
       *
       * const interestPayments = (existingLoan) => {
       *     return existingLoan ?
       *         dataWithCumulativeResults.map(scs => ({
       *             x: scs.startDate,
       *             y: scs.oldInterestCumulative
       *         })) :
       *         dataWithCumulativeResults.map(scs => ({
       *             x: scs.startDate,
       *             y: scs.oldInterestCumulative - scs.interestCostSavingsCumulative
       *         }));
       * }
       */

      // Normalizing data for a cumulative year
      const annualExistingLoanInterestCumulative = yearsOfData.map((year, idx) => ({
        year,
        data: runningCumulativeOldInterestReducedByYear[idx]
      }));

      const annualNewLoanInterestCumulative = yearsOfData.map((year, idx) => ({
        year,
        data: runningCumulativeNewInterestReducedByYear[idx]
      }));

      const interestPayments = existingLoan =>
      /*
       * Have to remove the last entry of the annual data, since it's almost never projected all the way through the end of the year
       * Unless we do some normalizing on the projection, this will give us the cleanest looking line
       */
        (existingLoan ?
          annualExistingLoanInterestCumulative.slice(0, -1).map(val => ({
            x: val.year,
            y: val.data
          })) :
          annualNewLoanInterestCumulative.slice(0, -1).map(val => ({
            x: val.year,
            y: val.data
          })));
      const formattedData = [
        {
          id: "Existing Loan Interest Payments",
          data: interestPayments(true)
        },
        {
          id: "New Loan Interest Payments",
          data: interestPayments(false)
        }
      ];

      setChartData(formattedData);
    }

    if (providedRefiLoanInterestPaid && providedExistingLoanInterestPaid) {
      const something = [
        {
          id: "Existing Loan Interest Payments",
          data: [
            {
              x: 0,
              y: 0
            },
            {
              x: term,
              y: totalExistingLoanInterestPaid
            }
          ]
        },
        {
          id: "New Loan Interest Payments",
          data: [
            {
              x: 0,
              y: 0
            },
            {
              x: term,
              y: totalRefiLoanInterestPaid
            }
          ]
        }
      ];
      setTotalCurrentLoanInterestPaid(providedExistingLoanInterestPaid);
      setTotalRefiLoanInterestPaid(providedRefiLoanInterestPaid);
      setChartData(something);
    }
  }, [breakEvenResults]);

  return (
    <>
      <div className="refi-line-interest-block-wrapper existing">
        <div className="refi-line-interest-text-wrapper">
          <span>Interest paid on the current loan</span>
          <span className="interest-text" style={{ color: companyPrimaryColor }}>{numeral(totalExistingLoanInterestPaid).format("$0,0")}</span>
        </div>
      </div>
      <div className="refi-line-interest-block-wrapper refinanced">
        <div className="refi-line-interest-text-wrapper">
          <span>Interest paid on the refi'd amount</span>
          <span className="interest-text" style={{ color: companySecondaryColor }}>{numeral(totalRefiLoanInterestPaid).format("$0,0")}</span>
        </div>
      </div>
      <ResponsiveLine
        data={chartData}
        height={172}
        width={640}
        colors={[companyPrimaryColor, companySecondaryColor]}
        margin={{ top: 32, right: 220, bottom: 24, left: 60 }}
        pointSize={8}
        lineWidth={4}
        axisLeft={{
          tickSize: 0,
          tickPadding: 10,
          format: val => ((Boolean(providedRefiLoanInterestPaid) && Boolean(providedExistingLoanInterestPaid)) ? `${val % 500000 === 0 ? `$${formatCurrencyNoDecimal(val)}` : ""}` : `$${formatCurrencyNoDecimal(val)}`)
        }}
        axisBottom={{
          tickSize: 0,
          tickPadding: 10,
          format: val => `${(term && term !== 0) ? `${val} yrs` : `${val}`}`
        }}
        theme={{
          axis: {
            domain: {
              line: {
                strokeWidth: 2,
                stroke: companyPrimaryColor
              }
            },
            ticks: {
              text: {
                fontFamily: "Work Sans",
                letterSpacing: 1,
                fontWeight: 300
              }
            }
          }
        }}
        layers={["lines", "points", "axes"]}
      />
    </>
  );
};

const mapStateToProps = ({ testHarness }) => ({
  breakEvenResults: testHarness?.refiBreakEvenResults?.data || []
});

export default compose(connect(mapStateToProps))(RefiAnalysisLineChart);
